import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  MenuItem,
  useTheme,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
} from "@mui/material";
import { 
  CloudUpload as CloudUploadIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ThemedDateTimePicker from "./ThemedDateTimePicker";
import API_BASE_URL from "../apiConfig";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  TRANSPARENT_PINK_05,
  TRANSPARENT_PINK_08,
  TRANSPARENT_PINK_2,
  TRANSPARENT_BLACK_08,
} from "../constants";

function MajorModal({ open, onClose, onMajorCreated, clubId, clubName, major = null }) {
  const theme = useTheme();
  const [form, setForm] = useState({
    title: "",
    start_date: dayjs(),
    end_date: dayjs().add(1, 'day'),
    content: "",
    show_title: true,
    events_header: "Tournament Schedule"
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (major) {
        // Edit mode
        setForm({
          title: major.title || "",
          start_date: major.start_date ? dayjs(major.start_date) : dayjs(),
          end_date: major.end_date ? dayjs(major.end_date) : dayjs().add(1, 'day'),
          content: major.content || "",
          show_title: major.show_title !== false,
          events_header: major.events_header || "Tournament Schedule"
        });
        setFilePreview(major.banner_url);
        setSelectedEvents(major.events || []);
      } else {
        // Create mode
        setForm({
          title: "",
          start_date: dayjs(),
          end_date: dayjs().add(1, 'day'),
          content: "",
          show_title: true,
          events_header: "Tournament Schedule"
        });
        setFilePreview(null);
        setSelectedEvents([]);
      }
      fetchAvailableEvents();
    }
  }, [open, major, clubId]);

  const fetchAvailableEvents = async () => {
    try {
      setEventsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/events`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const events = await response.json();
        setAvailableEvents(events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleEventToggle = (event) => {
    setSelectedEvents(prev => {
      const isSelected = prev.some(e => e.id === event.id);
      if (isSelected) {
        return prev.filter(e => e.id !== event.id);
      } else {
        return [...prev, { ...event, order: prev.length + 1 }];
      }
    });
  };

  const handleEventReorder = (fromIndex, toIndex) => {
    setSelectedEvents(prev => {
      const newEvents = [...prev];
      const [movedEvent] = newEvents.splice(fromIndex, 1);
      newEvents.splice(toIndex, 0, movedEvent);
      
      // Update order numbers
      return newEvents.map((event, index) => ({
        ...event,
        order: index + 1
      }));
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append('club_id', clubId);
      formData.append('title', form.title);
      formData.append('start_date', form.start_date.toISOString());
      formData.append('end_date', form.end_date.toISOString());
      formData.append('content', form.content);
      formData.append('show_title', form.show_title);
      formData.append('events_header', form.events_header);
      
      if (selectedFile) {
        formData.append('banner', selectedFile);
      }

      const url = major 
        ? `${API_BASE_URL}/api/majors/${major.id}`
        : `${API_BASE_URL}/api/majors`;
      
      const method = major ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save major');
      }

      const savedMajor = await response.json();

      // Assign events if any are selected
      if (selectedEvents.length > 0) {
        const eventIds = selectedEvents.map(event => event.id);
        const assignResponse = await fetch(`${API_BASE_URL}/api/majors/${savedMajor.id}/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: "include",
          body: JSON.stringify({ event_ids: eventIds }),
        });

        if (!assignResponse.ok) {
          console.error('Failed to assign events to major');
        }
      }

      setSuccess(major ? 'Major updated successfully!' : 'Major created successfully!');
      
      setTimeout(() => {
        onMajorCreated(savedMajor);
        onClose();
      }, 1000);

    } catch (error) {
      console.error('Error saving major:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
          borderRadius: 3,
          minHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: PRIMARY_PINK, 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        pt: 2
      }}>
        <Typography variant="h6" fontWeight="bold">
          {major ? 'Edit Major' : 'Create Major'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Basic Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Major Title"
                value={form.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                required
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.show_title}
                    onChange={(e) => handleInputChange('show_title', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                    }}
                  />
                }
                label="Show Title"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Events Section Header"
                value={form.events_header}
                onChange={(e) => handleInputChange('events_header', e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                helperText="Default: 'Tournament Schedule'"
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Event Dates
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <ThemedDateTimePicker
                      label="Start Date"
                      value={form.start_date}
                      onChange={(newValue) => handleInputChange('start_date', newValue)}
                      sx={{ flex: 1 }}
                    />
                    <ThemedDateTimePicker
                      label="End Date"
                      value={form.end_date}
                      onChange={(newValue) => handleInputChange('end_date', newValue)}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>

              <TextField
                fullWidth
                label="Additional Content"
                value={form.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                placeholder="Enter any additional information, rules, or details..."
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Banner Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Banner Image
              </Typography>
              
              {filePreview ? (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={filePreview}
                    alt="Banner preview"
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: `2px solid ${PRIMARY_PINK}`
                    }}
                  />
                  <IconButton
                    onClick={removeFile}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `2px dashed ${PRIMARY_PINK}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: TRANSPARENT_PINK_05,
                    }
                  }}
                  onClick={() => document.getElementById('banner-upload').click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: PRIMARY_PINK, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload banner image
                  </Typography>
                </Box>
              )}
              
              <input
                id="banner-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Box>
          </Grid>

          {/* Right Column - Event Assignment */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Assign Events ({selectedEvents.length} selected)
            </Typography>
            
            {eventsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {availableEvents.map((event) => {
                  const isSelected = selectedEvents.some(e => e.id === event.id);
                  return (
                    <Card
                      key={event.id}
                      sx={{
                        mb: 1,
                        border: isSelected ? `2px solid ${PRIMARY_PINK}` : '1px solid #e0e0e0',
                        backgroundColor: isSelected ? TRANSPARENT_PINK_05 : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? TRANSPARENT_PINK_08 : '#f5f5f5',
                        }
                      }}
                      onClick={() => handleEventToggle(event)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {event.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(event.start_time)}
                            </Typography>
                            {(event.location || event.location_url) && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                📍{' '}
                                {event.location_url ? (
                                  <Box
                                    component="a"
                                    href={event.location_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: PRIMARY_PINK,
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline'
                                      }
                                    }}
                                  >
                                    {event.location_display_name || event.location || 'TBD'}
                                  </Box>
                                ) : (
                                  event.location
                                )}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isSelected && (
                              <CheckCircleIcon sx={{ color: PRIMARY_PINK }} />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            {/* Selected Events Order */}
            {selectedEvents.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Event Order
                </Typography>
                <List sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedEvents.map((event, index) => (
                    <ListItem
                      key={event.id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: TRANSPARENT_PINK_05
                      }}
                    >
                      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <ListItemText
                        primary={`${index + 1}. ${event.title}`}
                        secondary={formatDateTime(event.start_time)}
                      />
                      <ListItemSecondaryAction>
                        <MuiIconButton
                          edge="end"
                          onClick={() => handleEventToggle(event)}
                          sx={{ color: PRIMARY_PINK }}
                        >
                          <DeleteIcon />
                        </MuiIconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5' }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: 'text.primary' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !form.title.trim()}
          sx={{
            backgroundColor: PRIMARY_PINK,
            color: 'white !important',
            '&:hover': {
              backgroundColor: PRIMARY_PINK_DARK,
              color: 'white !important',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="white" />
          ) : (
            major ? 'Update Major' : 'Create Major'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MajorModal;
